'use client'

import { motion } from 'framer-motion'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { type ProductVariant, productStyles } from './product-styles'

interface VideoShowcaseSectionProps {
  variant: ProductVariant
  eyebrow?: string
  title: string
  description: string
  videoId: string
}

export function VideoShowcaseSection({
  variant,
  eyebrow,
  title,
  description,
  videoId,
}: VideoShowcaseSectionProps) {
  const styles = productStyles[variant]

  return (
    <Section background={styles.background === 'dark' ? 'gray' : styles.background}>
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-10"
        >
          {eyebrow && (
            <p className={`text-micro-uppercase ${styles.eyebrow} mb-3`}>{eyebrow}</p>
          )}
          <h2 className={`text-display-1 ${styles.title} mb-4`}>{title}</h2>
          <p className={`text-body-lg ${styles.subtitle} max-w-2xl mx-auto`}>{description}</p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.98 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.1 }}
          className="relative aspect-video w-full max-w-5xl mx-auto rounded-2xl overflow-hidden border border-line shadow-xl bg-raised"
        >
          <iframe
            src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0`}
            title={title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full"
          />
        </motion.div>
      </Container>
    </Section>
  )
}
